import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { marketGet } from "../arena/marketApi";

export const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
export const axCfg = { headers: { "X-API-Key": process.env.REACT_APP_API_KEY || "" } };

// Operator time zone (America/Chicago today) — every time on the tab uses it.
const fallbackTz = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
})();
export const MarketsCtx = createContext({ tz: fallbackTz });
export const useMarkets = () => useContext(MarketsCtx);

// Fetch + poll with an explicit loading → data | error state. A slow endpoint
// shows a skeleton, then an error with Retry after `timeoutMs` — never a zero.
export function useFeed(url, { interval = 0, timeoutMs = 25000, enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: !!url && enabled });
  const alive = useRef(true);
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!url || !enabled) return;
    const my = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: s.data ? null : s.error }));
    marketGet(url, { ...axCfg, timeout: timeoutMs })
      .then((r) => {
        if (!alive.current || my !== seq.current) return;
        if (r.data && r.data.error && Object.keys(r.data).length <= 2) throw new Error(r.data.error);
        setState({ data: r.data, error: null, loading: false });
      })
      .catch((e) => {
        if (!alive.current || my !== seq.current) return;
        const msg = e?.code === "ECONNABORTED" ? "Timed out waiting for the server." : (e?.message || "Request failed.");
        setState((s) => ({ data: s.data, error: msg, loading: false }));
      });
  }, [url, enabled, timeoutMs]);

  useEffect(() => {
    alive.current = true;
    load();
    if (!interval) return () => { alive.current = false; };
    const id = setInterval(load, interval);
    return () => { alive.current = false; clearInterval(id); };
  }, [load, interval]);

  return { ...state, retry: load };
}

export function Card({ title, scope, actions, children, className = "", bodyClass = "", id }) {
  return (
    <section className={`mk-card ${className}`} id={id}>
      {(title || actions) ? (
        <header className="mk-card-head">
          <div>
            {title ? <h3>{title}</h3> : null}
            {scope ? <p className="mk-scope">{scope}</p> : null}
          </div>
          {actions ? <div className="mk-card-actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={`mk-card-body ${bodyClass}`}>{children}</div>
    </section>
  );
}

export function Skeleton({ rows = 3, height }) {
  return (
    <div className="mk-skel" aria-busy="true" aria-label="Loading" style={height ? { minHeight: height } : undefined}>
      {Array.from({ length: rows }, (_, i) => <span key={i} style={{ width: `${90 - i * 13}%` }} />)}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="mk-error" role="alert">
      <p>{message || "This panel could not load."}</p>
      {onRetry ? (
        <button type="button" className="mk-btn" onClick={onRetry}><RefreshCw aria-hidden /> Retry</button>
      ) : null}
    </div>
  );
}

// Renders children(data) once loaded; skeleton before, error + retry on failure.
export function FeedBody({ feed, rows = 3, height, children, empty }) {
  if (feed.data == null) {
    if (feed.error) return <ErrorState message={feed.error} onRetry={feed.retry} />;
    return <Skeleton rows={rows} height={height} />;
  }
  if (empty && empty(feed.data)) return <p className="mk-empty">{typeof empty === "function" && empty.message ? empty.message : "Nothing to show yet."}</p>;
  return children(feed.data);
}

export function Seg({ options, value, onChange, label }) {
  return (
    <div className="mk-seg" role="group" aria-label={label}>
      {options.map((o) => {
        const v = typeof o === "object" ? o.value : o;
        const l = typeof o === "object" ? o.label : o;
        return (
          <button key={v} type="button" className={value === v ? "is-on" : ""} aria-pressed={value === v}
            onClick={() => onChange(v)}>{l}</button>
        );
      })}
    </div>
  );
}

export const Dot = ({ color, title }) => (
  <i className="mk-dot" style={{ background: color }} title={title} aria-label={title} role="img" />
);
